import * as THREE from 'three';
import {
  PELL_ZONE_ORDER,
  TARGETS_SPAWN_DISTANCE_M,
  TARGETS_SPREAD_RAD,
  TARGETS_ZONE_WEIGHTS,
  type Zone,
} from '../config/constants';
import { getZoneBands } from '../setup/zoneBands';

/**
 * Frozen reference frame captured at session start.
 * - `originXZ`: floor position projected from camera at start (Y = 0).
 * - `forwardYaw`: angle (rad) around +Y from world -Z to the user's facing
 *    direction at start. spawnDirection = rotateY(forwardYaw + angularOffset).
 */
export type SpawnAnchor = {
  originXZ: THREE.Vector3;
  forwardYaw: number;
};

export function captureSpawnAnchor(camera: THREE.Camera): SpawnAnchor {
  const camPos = new THREE.Vector3();
  camera.getWorldPosition(camPos);
  const originXZ = new THREE.Vector3(camPos.x, 0, camPos.z);

  const fwd = new THREE.Vector3();
  camera.getWorldDirection(fwd);
  // Yaw on the horizontal plane.
  const forwardYaw = Math.atan2(fwd.x, fwd.z); // matches THREE convention for -Z forward
  return { originXZ, forwardYaw };
}

/**
 * Sample a zone using weighted distribution from TARGETS_ZONE_WEIGHTS.
 * Assumes weights sum to ~1.
 */
export function sampleZone(rng: () => number = Math.random): Zone {
  const r = rng();
  let acc = 0;
  for (const z of PELL_ZONE_ORDER) {
    acc += TARGETS_ZONE_WEIGHTS[z];
    if (r <= acc) return z;
  }
  return PELL_ZONE_ORDER[PELL_ZONE_ORDER.length - 1] ?? 'chest';
}

/**
 * Compute a world-space spawn position for the given anchor.
 * Y is sampled uniformly within the zone's vertical band (fraction of user height).
 * Horizontal angle is uniform in [-TARGETS_SPREAD_RAD, +TARGETS_SPREAD_RAD] around forwardYaw.
 * `distanceM` is the radial distance from the user (tunable in settings).
 */
export function computeSpawnPosition(
  anchor: SpawnAnchor,
  userHeightM: number,
  zone: Zone,
  distanceM: number = TARGETS_SPAWN_DISTANCE_M,
  rng: () => number = Math.random,
): THREE.Vector3 {
  const [yLow, yHigh] = getZoneBands()[zone];
  const yFrac = yLow + (yHigh - yLow) * rng();
  const y = yFrac * userHeightM;

  const angle = anchor.forwardYaw + (rng() * 2 - 1) * TARGETS_SPREAD_RAD;
  // Forward unit vector for this angle (atan2(x, z) inverse).
  const dirX = Math.sin(angle);
  const dirZ = Math.cos(angle);
  const x = anchor.originXZ.x + dirX * distanceM;
  const z = anchor.originXZ.z + dirZ * distanceM;
  return new THREE.Vector3(x, y, z);
}
